/* import __COLOCATED_TEMPLATE__ from './sq-auto-viewport.hbs'; */
import { scheduleOnce } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@ember/component';
import $ from 'jquery';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  attributeBindings: ['style'],
  // the element which has a height set and will be the container for this component
  offsetParentSelector: null,
  // which element will the scrolling occur on
  scrollParentSelector: null,
  // NB:- scrolling this viewport will also trigger resizes elsewhere

  // note(seung): how far from the top of the viewport we should position the scroller
  // mainly used to adjust for the new nav taking a fixed position on the top of the window
  offsetFromViewportTop: 0,
  heightOffset: 0,
  minHeight: null,
  environment: service(),
  async didInsertElement() {
    this._super();
    const elementId = this.elementId;
    const $window = $(window); // eslint-disable-line ember/no-jquery
    $window.on(`resize.${elementId}`, this.windowDidResize.bind(this));
    this.scrollParent().on(`scroll.${elementId}`, () => $window.resize());
    await this.marketComponentLoad();
    scheduleOnce('afterRender', this, 'calculateAndApplyStyles');
  },
  didUpdateAttrs() {
    this._super();
    // Ensure styles are updated whenever the heightOffset changes
    this.calculateAndApplyStyles();
  },
  // Checks if any parent components are market web-components and triggers a resize after they load
  // This corrects an issue where web-components report 0 height until after they load, which is async
  async marketComponentLoad() {
    const promises = [];
    function waitForDidLoad(promises, elm) {
      if (Boolean(elm) && elm.nodeType === 1) {
        const parentElement = elm.parentElement;
        if (parentElement && parentElement.tagName.startsWith('MARKET-') && typeof parentElement.componentOnReady === 'function') {
          promises.push(parentElement.componentOnReady());
        }
        waitForDidLoad(promises, parentElement);
      }
    }
    waitForDidLoad(promises, this.element);
    await Promise.all(promises);
  },
  willDestroyElement() {
    this._super();
    const elementId = this.elementId;
    $(window).off(`resize.${elementId}`); // eslint-disable-line ember/no-jquery
    this.scrollParent().off(`scroll.${elementId}`);
  },
  calculateAndApplyStyles() {
    let viewportTop = null;
    let winHeight = null;
    const $window = $(window); // eslint-disable-line ember/no-jquery
    const useWindowScrollPosition = this.scrollParent()[0] === window;
    const scrollTop = useWindowScrollPosition ? $window.scrollTop() : 0;
    if (this.environment.isTesting) {
      const $viewport = $('#ember-testing-container'); // eslint-disable-line ember/no-jquery
      viewportTop = $viewport.offset().top;
      winHeight = $viewport.height();
      const {
        y: {
          value: yScale
        }
      } = $viewport[0].computedStyleMap().get('transform')[0];
      // In legacy tests, the ember testing container's calculated height incorporates the scaling.
      // In addon tests, the scaling is applied to #ember-testing instead, so we need to readjust.
      if (yScale === 0) {
        winHeight *= 2;
      }
    } else {
      viewportTop = this.offsetFromViewportTop;
      winHeight = $window.height() - this.offsetFromViewportTop;
    }
    winHeight += this.heightOffset;
    const offsetParentSelector = this.offsetParentSelector;
    const $offsetParent = $(offsetParentSelector || this.element.parentElement); // eslint-disable-line ember/no-jquery

    // Measure from the top of the offset parent.
    // If the scroll parent is the window, take scroll position into account.
    // If the parent's top is negative (scrolled up beyond the viewport), negate the
    // value so that the element stays visible (at the top of the viewport).
    const parentTop = $offsetParent.offset().top - viewportTop - scrollTop;
    const top = parentTop <= 0 ? -parentTop : 0;

    // transform: translateY(0px) should work just fine, but doesn't when
    // 1) the browser is Safari on ios, and
    // 2) there is Japanese text.
    // In that case, stuff just is just randomly invisible. If we omit the
    // transform entirely, everything's fine. For more information, see
    // https://block.atlassian.net/browse/ONCALL-158317
    const transform = top === 0 ? null : `translateY(${top}px)`;
    const elementHeight = parentTop <= 0 ? winHeight : winHeight - parentTop;
    let height;
    if (this.minHeight && elementHeight < this.minHeight) {
      height = `${this.minHeight}px`;
    } else {
      height = `${elementHeight - 2}px`; // -2 fixes a scroll bug in Chrome
    }
    let style = '';
    if (transform) {
      style += `transform: ${transform}; `;
    }
    style += `height: ${height}; `;
    // Apparently, there are consumers of this component which implicitly expect
    // that it introduces a new stacking context (https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_positioned_layout/Understanding_z-index/Stacking_context).
    // I don't know exactly how or why, but the "edit item" page is one of them.
    // It used to be that the "transform: translateY(...)" above happened to
    // satisfy that requirement, but now that it's not always there, we do this
    // to ensure it:
    style += 'position: relative; ';
    this.set('style', htmlSafe(style));
  },
  windowDidResize() {
    this.calculateAndApplyStyles();
  },
  scrollParent() {
    if (this.scrollParentSelector === 'window') {
      return $(window); // eslint-disable-line ember/no-jquery
    } else {
      return $(this.element).parents(this.scrollParentSelector); // eslint-disable-line ember/no-jquery
    }
  }
});